import { fromJS, OrderedMap } from 'immutable'
import _get from 'lodash/get'
import _pick from 'lodash/pick'
import { createCSV, MAX_PAGE_SIZE } from '../convert-to-csv'
import { showSuccessToast } from '../../../toast/redux/actions'
import {
  GET_MERCHANDISING_CAMPAIGNS_DATA,
  setMerchandisingCampaignsData,
  setMerchandisingCampaignsError,
  UPDATE_MERCHANDISING_CAMPAIGN,
  updateMerchandisingCampaignSuccess,
  updateMerchandisingCampaignFailure,
  DELETE_MERCHANDISING_CAMPAIGN,
  deleteMerchandisingCampaignSuccess,
  deleteMerchandisingCampaignFailure,
  CREATE_MERCHANDISING_CAMPAIGN,
  createMerchandisingCampaignSuccess,
  CREATE_MERCHANDISING_CAMPAIGN_SUCCESS,
  createMerchandisingCampaignFailure,
  CREATE_CAMPAIGN_CONTENT,
  createCampaignContentSuccess,
  createCampaignContentFailure,
  UPDATE_CAMPAIGN_CONTENT,
  updateCampaignContentSuccess,
  updateCampaignContentFailure,
  DOWNLOAD_MERCHANDISING_CAMPAIGNS,
  downloadMerchandisingCampaignsError,
  GET_HAS_PREVIEW_MEDIA,
  getHasPreviewMedia,
  setHasPreviewMedia,
  GET_ONBOARD_CHOICES,
  setOnboardChoices,
  CREATE_MERCHANDISING_CAMPAIGN_WITH_CONTENT,
  CREATE_MERCHANDISING_CAMPAIGN_WITH_CONTENT_SUCCESS,
  createMerchandisingCampaignWithContentSuccess,
  createMerchandisingCampaignWithContentFailure,
} from './actions'
import {
  getMerchandisingCampaigns,
  createMerchandisingCampaign,
  updateMerchandisingCampaign,
  deleteMerchandisingCampaign,
  createCampaignContent,
  updateCampaignContent,
  hasPreviewMedia,
  getOnboard,
  createMerchandisingCampaignWithContent,
} from '..'

const log = console

async function getMerchandisingCampaignsDataHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const {
    page,
    perPage,
    slug,
    placement,
    viewMode,
    isActive,
    dateRange,
    placementLanguage,
    weight,
  } = action.payload

  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      page,
      perPage,
      slug,
      placement,
      viewMode,
      isActive,
      dateRange,
      placementLanguage,
      weight,
    }

    const data = await getMerchandisingCampaigns(options)

    // transform to map keyed by campaignId
    const campaigns = fromJS(_get(data, 'campaigns'))
    const mapped = campaigns.reduce(
      (acc, campaign) => acc.set(campaign.get('id'), campaign),
      OrderedMap(),
    )

    store.dispatch(
      setMerchandisingCampaignsData({
        data: mapped,
        page: _get(data, 'page'),
        perPage: _get(data, 'perPage'),
        totalCount: _get(data, 'totalCount'),
      }),
    )
  } catch (error) {
    log.error(error, 'Error getting merchandising content data.')
    const errorParsed = JSON.parse(_get(error, ['response', 'text']))
    const message = _get(errorParsed, 'message')
    store.dispatch(
      setMerchandisingCampaignsError({
        error: message,
        language: placementLanguage,
      }),
    )
  }
}

async function downloadMerchandisingCampaignsHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { language, page, slug, placement, viewMode, isActive, dateRange, weight } = action.payload

  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      language,
      page,
      perPage: MAX_PAGE_SIZE,
      slug,
      placement,
      viewMode,
      isActive,
      dateRange,
      weight,
    }
    const data = await getMerchandisingCampaigns(options)
    const campaigns = _get(data, 'campaigns')
    if (campaigns) {
      createCSV(campaigns)
    }
  } catch (error) {
    const errorParsed = JSON.parse(_get(error, ['response', 'text']))
    const message = _get(errorParsed, 'message')
    log.error(error, 'Error downloading campaigns.')
    store.dispatch(
      downloadMerchandisingCampaignsError({
        error: message,
        language,
      }),
    )
  }
}

async function createMerchandisingCampaignHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { data } = action.payload
  const campaign = _pick(data.toJS(), [
    'startTs',
    'placement',
    'viewMode',
    'slug',
    'isActive',
    'language',
    'endTs',
    'weight',
  ])

  // toJS was stripping array values, needed to do this seperate
  const targeting = data.get('campaignTargeting').toJS()
  campaign.campaignTargeting = targeting

  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      campaign,
    }

    const result = await createMerchandisingCampaign(options)
    store.dispatch(createMerchandisingCampaignSuccess(fromJS(result)))
    alert('campaign created!')
  } catch (error) {
    log.error(error, 'Error creating campaign')
    const errorList = _get(error, ['response', 'text'])
    const errorsParsed = _get(JSON.parse(errorList), 'errors')
    store.dispatch(
      createMerchandisingCampaignFailure({
        errorsParsed: fromJS(errorsParsed),
        campaign: data,
      }),
    )
    alert(`failed to create campaign, ${_get(errorsParsed, [0, 'message'])}`)
  }
}

async function updateMerchandisingCampaignHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { data } = action.payload
  const campaignId = _get(data.toJS(), 'id')
  const campaign = _pick(data.toJS(), [
    'startTs',
    'placement',
    'viewMode',
    'slug',
    'isActive',
    'language',
    'endTs',
    'weight',
  ])

  // toJS was stripping array values, needed to do this seperate
  const targeting = data.get('campaignTargeting').toJS()
  campaign.campaignTargeting = targeting

  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      campaign,
      campaignId,
    }
    const result = await updateMerchandisingCampaign(options)
    const contentId = _get(result, 'contentItems[0].contentId')
    store.dispatch(updateMerchandisingCampaignSuccess(fromJS(result)))
    store.dispatch(showSuccessToast('Campaign updated!'))
    store.dispatch(getHasPreviewMedia({ contentId, campaignId }))
  } catch (error) {
    log.error(error, 'Error updating campaign')
    const errorList = _get(error, ['response', 'text'])
    const errorsParsed = _get(JSON.parse(errorList), 'errors')
    store.dispatch(
      updateMerchandisingCampaignFailure({
        errorsParsed: fromJS(errorsParsed),
        campaign: data,
      }),
    )
  }
}

async function deleteMerchandisingCampaignHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { data } = action.payload
  const campaignId = _get(data.toJS(), 'id')

  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      campaignId,
    }
    await deleteMerchandisingCampaign(options)
    store.dispatch(deleteMerchandisingCampaignSuccess({ campaignId }))
    store.dispatch(showSuccessToast('Campaign deleted successfully!'))

    // Refresh the campaign list to remove the deleted campaign
    store.dispatch({
      type: 'GET_MERCHANDISING_CAMPAIGNS_DATA',
      payload: {},
    })
  } catch (error) {
    log.error(error, 'Error deleting campaign')
    const errorList = _get(error, ['response', 'text'])
    const errorsParsed = _get(JSON.parse(errorList), 'errors')
    store.dispatch(
      deleteMerchandisingCampaignFailure({
        errorsParsed: fromJS(errorsParsed),
        campaign: data,
      }),
    )
  }
}

async function createCampaignContentHandler ({ store, action }) {
  const { config, auth, campaigns } = store.getState()
  const campaignContent = action.payload
  const campaignId = campaignContent.get('campaignId')
  const campaignLanguage = campaigns.getIn(['data', campaignId, 'language'])
  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      campaignContent,
      language: campaignLanguage,
    }
    const data = await createCampaignContent(options)
    store.dispatch(createCampaignContentSuccess(fromJS(data)))
    store.dispatch(showSuccessToast('Campaign content updated!'))
  } catch (error) {
    log.error(error, 'Error creating campaign')
    store.dispatch(
      createCampaignContentFailure({
        error,
        campaignContent,
        campaignId,
      }),
    )
  }
}

async function updateCampaignContentHandler ({ store, action }) {
  const { config, auth, campaigns } = store.getState()
  const campaignContent = action.payload
  const campaignId = campaignContent.get('campaignId')
  const campaignLanguage = campaigns.getIn(['data', campaignId, 'language'])
  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      campaignContent,
      id: action.id,
      language: campaignLanguage,
    }
    const data = await updateCampaignContent(options)
    store.dispatch(updateCampaignContentSuccess(fromJS(data)))
    store.dispatch(showSuccessToast('Campaign content updated!'))
  } catch (error) {
    log.error(error, 'Error creating campaign')
    const errorParsed = JSON.parse(_get(error, ['response', 'text']))
    const message = _get(errorParsed, ['errors', 0, 'message'])
    store.dispatch(
      updateCampaignContentFailure({
        error: message,
        campaignContent,
        campaignId,
      }),
    )
  }
}

async function getHasPreviewMediaHandler ({ store, action }) {
  const { config, auth } = store.getState()
  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      id: action.payload.contentId,
    }
    const data = await hasPreviewMedia(options)

    store.dispatch(
      setHasPreviewMedia({
        hasPreviewMedia: data,
        campaignId: action.payload.campaignId,
      }),
    )
  } catch (error) {
    log.error(error, 'Error getting preview media')
    alert('Error getting preview status')
  }
}

async function getOnboardDataHandler ({ store }) {
  const { config, auth } = store.getState()
  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    }
    const results = await getOnboard(options)
    const onboardChoices = fromJS(results)
    store.dispatch(setOnboardChoices({ onboardChoices }))
  } catch (error) {
    log.error(error, 'Error getting onboard data')
  }
}

async function createMerchandisingCampaignWithContentHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { data } = action.payload

  // Extract campaign fields
  const campaign = _pick(data.toJS(), [
    'startTs',
    'placement',
    'viewMode',
    'slug',
    'isActive',
    'language',
    'endTs',
    'weight',
  ])

  // Add campaign targeting
  const targeting = data.get('campaignTargeting').toJS()
  campaign.campaignTargeting = targeting

  // Add campaign content if present
  const contentItem = data.get('contentItem')
  if (contentItem) {
    campaign.contentItem = {
      contentType: contentItem.get('contentType'),
      contentId: parseInt(contentItem.get('contentId'), 10),
      label: contentItem.get('label'),
    }
  }

  try {
    const options = {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      campaign,
    }

    const result = await createMerchandisingCampaignWithContent(options)
    store.dispatch(createMerchandisingCampaignWithContentSuccess(fromJS(result)))
    alert('Campaign with content created successfully!')
  } catch (error) {
    log.error(error, 'Error creating complete campaign')
    const errorList = _get(error, ['response', 'text'])
    const errorsParsed = _get(JSON.parse(errorList), 'errors')
    store.dispatch(
      createMerchandisingCampaignWithContentFailure({
        error: errorsParsed,
        campaign: data,
      }),
    )
    alert(`Failed to create campaign: ${_get(errorsParsed, [0, 'message'])}`)
  }
}

export default (store) => (next) => (action) => {
  const { type } = action
  switch (type) {
    case GET_MERCHANDISING_CAMPAIGNS_DATA:
    case CREATE_MERCHANDISING_CAMPAIGN_SUCCESS:
    case CREATE_MERCHANDISING_CAMPAIGN_WITH_CONTENT_SUCCESS:
      getMerchandisingCampaignsDataHandler({ store, action })
      break
    case CREATE_MERCHANDISING_CAMPAIGN:
      createMerchandisingCampaignHandler({ store, action })
      break
    case CREATE_MERCHANDISING_CAMPAIGN_WITH_CONTENT:
      createMerchandisingCampaignWithContentHandler({ store, action })
      break
    case UPDATE_MERCHANDISING_CAMPAIGN:
      updateMerchandisingCampaignHandler({ store, action })
      break
    case DELETE_MERCHANDISING_CAMPAIGN:
      deleteMerchandisingCampaignHandler({ store, action })
      break
    case CREATE_CAMPAIGN_CONTENT:
      createCampaignContentHandler({ store, action })
      break
    case UPDATE_CAMPAIGN_CONTENT:
      updateCampaignContentHandler({ store, action })
      break
    case DOWNLOAD_MERCHANDISING_CAMPAIGNS:
      downloadMerchandisingCampaignsHandler({ store, action })
      break
    case GET_HAS_PREVIEW_MEDIA:
      getHasPreviewMediaHandler({ store, action })
      break
    case GET_ONBOARD_CHOICES:
      getOnboardDataHandler({ store })
      break
    default:
      break
  }
  next(action)
}
